import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { AlertCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchItemMaterialOptions,
  fetchItemMaterialSizeOptions,
  getEditItem,
  getIsItemModalOpen,
  getItemMaterialOptions,
  getItemMaterialSizeOptions,
  toggleItemModal,
} from "../../features/items/itemsSlice";
import { IFormInput } from "../../types/item";
import { t } from "../../locales";
import { filterSelectStyles } from "../../static/styles";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ImageCropUpload from "../common/ImageCropUpload";

const modalInputClass = (hasError: boolean) =>
  `h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalTextareaClass = (hasError: boolean) =>
  `text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

type Props = {
  handleSaveItem: (data: IFormInput) => void;
};

const ItemFormModal = ({ handleSaveItem }: Props) => {
  const dispatch = useAppDispatch();
  const editItem = useAppSelector(getEditItem);
  const isItemModalOpen = useAppSelector(getIsItemModalOpen);
  const materialOptions = useAppSelector(getItemMaterialOptions);
  const materialSizeOptions = useAppSelector(getItemMaterialSizeOptions);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  // Cascading picker: which Material is selected narrows the Material Size
  // dropdown below it. This is UI-only — only material_id (the chosen
  // Material Size's id) is actually submitted.
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", description: "", material_id: null },
  });

  useEffect(() => {
    if (isItemModalOpen) {
      dispatch(fetchItemMaterialOptions());
    }
  }, [isItemModalOpen, dispatch]);

  useEffect(() => {
    if (isItemModalOpen) {
      dispatch(fetchItemMaterialSizeOptions(selectedMaterialId ?? undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isItemModalOpen, selectedMaterialId, dispatch]);

  // Once a material's sizes load, pre-select its default size instead of
  // leaving the picker empty — saves a click on the common case where a
  // material only really has one size worth using.
  useEffect(() => {
    if (!selectedMaterialId || getValues("material_id")) return;
    const defaultOption = materialSizeOptions.find((o) => o.is_default);
    if (defaultOption) {
      setValue("material_id", defaultOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialSizeOptions, selectedMaterialId]);

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveItem({ ...data, image: imageBase64 });
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    setImageBase64(null);
    setSelectedMaterialId(null);
    dispatch(toggleItemModal(false));
  };

  useEffect(() => {
    if (editItem && editItem.id) {
      reset({
        name: editItem.name,
        description: editItem.description,
        material_id: editItem.material_id,
      });
      setSelectedMaterialId(editItem.material_size?.material_id ?? null);
      setImageBase64(null);
    } else {
      reset({ name: "", description: "", material_id: null });
      setSelectedMaterialId(null);
      setImageBase64(null);
    }
  }, [editItem, isItemModalOpen]);

  return (
    <Dialog
      open={isItemModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.items.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editItem ? `Editing: ${editItem.name}` : t.items.addNewItem}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-semibold text-[#113F67]">{t.items.nameLabel} <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              placeholder={t.items.namePlaceholder}
              {...register("name", { required: true })}
              className={modalInputClass(!!errors.name)}
            />
            {errors.name && errors.name.type === "required" && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.items.nameRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="material_id" className="text-sm font-semibold text-[#113F67]">{t.items.materialLabel}</Label>
            <ReactSelect
              inputId="material_id"
              styles={filterSelectStyles}
              options={materialOptions}
              placeholder={t.items.materialPlaceholder}
              isClearable
              value={materialOptions.find((o) => o.value === selectedMaterialId) || null}
              onChange={(opt: any) => {
                setSelectedMaterialId(opt?.value || null);
                setValue("material_id", null);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="material_size_id" className="text-sm font-semibold text-[#113F67]">{t.items.materialSizeLabel}</Label>
            <Controller
              name="material_id"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  inputId="material_size_id"
                  styles={filterSelectStyles}
                  options={materialSizeOptions}
                  placeholder={selectedMaterialId ? t.items.materialSizePlaceholder : t.items.selectMaterialFirst}
                  isClearable
                  isDisabled={!selectedMaterialId}
                  value={materialSizeOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value || null)}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-[#113F67]">{t.items.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t.items.descriptionPlaceholder}
              {...register("description", { required: false })}
              className={modalTextareaClass(false)}
            />
          </div>

          <ImageCropUpload
            value={imageBase64}
            onChange={setImageBase64}
            label={t.items.imageLabel}
            existingImageUrl={editItem?.image}
          />

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.items.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.items.saving : t.items.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ItemFormModal;
